/** App shell — full-width header band with AI command bar, streak stat, and avatar menu. */
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Flame, Menu as MenuIcon, Play } from 'lucide-react';
import type { ContinueResponse } from '@formency/shared';
import { getDisplayName, getInitials } from '@formency/shared';
import { useAuth } from '@/shared/auth/AuthProvider';
import {
  useProfile,
  useNotifications,
  useMyStats,
  useMySuggestions,
  useCreateReviseSession,
  useContinue,
} from '@/shared/api/hooks';
import { getNotificationLink } from '@/shared/utils/notificationLinks';
import { playNotificationSound } from '@/shared/utils/notificationSound';
import { useSessionGuard } from '@/shared/hooks/useSessionGuard';
import { ExpandableMenu } from './ExpandableMenu';
import { prefetchPrimaryRoutes } from './prefetchConfig';
import Logomark from '@/shared/components/Logomark';
import LearnerTopLoadingBar from '@/shared/components/LearnerTopLoadingBar';
import { LEARNER_LAYOUT_BREAKPOINTS } from '../theme/layout';
import {
  CommandBar,
  CommandBarActions,
  CommandBarThread,
  CommandBarTrigger,
  CommandBarModal,
  useCommandBarChat,
} from './CommandBar';
import { buildCommandBarPlaceholders } from './CommandBar/placeholders';

const SHELL_BREAKPOINTS = {
  desktop: LEARNER_LAYOUT_BREAKPOINTS.singleDesktopPx,
  doubleDesktop: LEARNER_LAYOUT_BREAKPOINTS.doubleDesktopPx,
} as const;

const SHELL_SCROLL = {
  topReset: 2,
  activateAfter: 140,
  activateViewportRatio: 0.36,
  activateViewportMin: 240,
  activateViewportMax: 560,
  stepThreshold: 2,
  activateUpDistance: 44,
  activateMaxUpVelocityPxPerMs: 1.6,
  activateVelocityCooldownMs: 140,
  revealUpDistance: 56,
  hideDownDistance: 42,
  edgeEpsilon: 1,
  shortPageSlack: 220,
} as const;

const SHELL_MOTION = {
  easeStandard: 'cubic-bezier(0.32, 0.72, 0, 1)',
  easeEmphasized: 'cubic-bezier(0.22, 1, 0.36, 1)',
  headerRevealDurationMs: 360,
  headerEntranceDurationMs: 500,
  sheetHeightDurationMs: 420,
  sheetFadeDurationMs: 240,
  sheetToneDurationMs: 220,
  overlayFadeDurationMs: 400,
  streakWidthDurationMs: 520,
  streakTextDurationMs: 340,
  streakFadeDurationMs: 240,
  streakRevealDelayMs: 140,
} as const;

const SHELL_DIMENSIONS = {
  panelBottomPadDesktop: 12,
  panelBottomPadMobile: 10,
  navHeightDesktop: 74,
  navHeightMobile: 60,
  blockPadDesktop: 10,
  blockPadMobile: 8,
  streakPillDesktop: 44,
  streakPillMobile: 40,
  avatarDesktop: 35,
  avatarMobile: 33,
  logoDesktop: 50,
  logoMobile: 44,
  streakTextCharWidth: 7.4,
  streakDigitCharWidth: 8,
  streakTextGap: 8,
  streakIconTextGap: 4,
  streakTextPadLeft: 10,
  flameDesktop: 15,
  flameMobile: 14,
} as const;

const SHELL_GLASS = {
  background: 'var(--shell-surface-bg)',
  backdrop: 'blur(20px) saturate(1.22)',
  border: 'var(--shell-surface-border)',
  shadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
} as const;

// ─── Avatar ──────────────────────────────────────────────────────────────────

function Avatar({ hasUnread = false, size = 34 }: { hasUnread?: boolean; size?: number }) {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const userShape = { name: user?.name ?? null, email: user?.email ?? null };
  const initials = getInitials(userShape);
  const displayName = getDisplayName(userShape);
  const avatarUrl = profile?.avatarUrl ?? null;
  const [imgError, setImgError] = useState(false);

  const showImage = !!avatarUrl && !imgError;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <div
        className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full font-semibold"
        style={{
          background: showImage
            ? 'var(--color-surface-muted)'
            : 'linear-gradient(135deg, #E0A468, #D580A0, #9880C8)',
          color: showImage ? 'var(--color-accent)' : '#fff',
          fontSize: Math.max(12, Math.round(size * 0.34)),
          letterSpacing: '0.02em',
        }}
      >
        {showImage ? (
          <img
            src={avatarUrl}
            alt={displayName}
            className="absolute inset-0 h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          initials
        )}
      </div>

      {hasUnread && (
        <span
          className="absolute rounded-full"
          style={{
            top: -1,
            right: -1,
            width: Math.max(9, Math.round(size * 0.24)),
            height: Math.max(9, Math.round(size * 0.24)),
            background: '#ef4444',
            border: '2px solid var(--shell-avatar-ring)',
          }}
        />
      )}
    </div>
  );
}

function ShellNavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex h-10 items-center rounded-full px-3.5 transition-colors hover:bg-[var(--color-surface-hover)]"
      style={{
        color: 'var(--shell-stat-text)',
        fontSize: 'var(--shell-nav-font-size)',
        fontWeight: 'var(--shell-nav-font-weight)',
        lineHeight: 1,
      }}
    >
      {children}
    </Link>
  );
}

function CommandPanelPlaceholder() {
  return (
    <aside className="hidden h-full min-w-0 py-4 learner-double:block">
      <div
        className="flex h-full min-h-32 items-center justify-center rounded-[1.45rem] px-5 text-center"
        style={{
          background: 'color-mix(in srgb, var(--color-surface-solid) 58%, transparent)',
          border: '1px dashed var(--color-border)',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--text-xs)',
          lineHeight: 'var(--leading-snug)',
        }}
      >
        Contextual panel placeholder
      </div>
    </aside>
  );
}

type ShellContinueData = Extract<ContinueResponse, { type: 'session' }>;

function sessionHref(data: Extract<ContinueResponse, { type: 'session' }>): string {
  if (data.sessionType === 'revise') return '/revision/session';
  if (data.courseId && data.lessonId) return `/courses/${data.courseId}/${data.lessonId}`;
  if (data.lessonId) return `/lessons/${data.folderSlug ?? 'unfiled'}/${data.lessonId}`;
  return '/';
}

function isShellContinueData(data: ContinueResponse | undefined): data is ShellContinueData {
  return data?.type === 'session';
}

function FloatingResumeButton({ hidden = false }: { hidden?: boolean }) {
  const { data } = useContinue();
  const navigate = useNavigate();

  if (hidden || !isShellContinueData(data)) return null;

  const progressPct = Math.max(0, Math.min(100, Math.round(data.progressPct)));
  const label = 'Resume';
  const title = data.title;
  const status = data.sessionType === 'revise' ? 'Revision in progress' : 'Lesson in progress';

  const handleClick = () => {
    navigate(sessionHref(data));
  };

  return (
    <button
      type="button"
      className="floating-resume-dock"
      onClick={handleClick}
      aria-label={`${label} ${title}, ${progressPct}% complete`}
    >
      <span className="floating-resume-dock__content">
        <span className="floating-resume-dock__status">{status}</span>
        <span className="floating-resume-dock__title">{title}</span>
      </span>
      <span className="floating-resume-dock__action">
        <Play size={15} strokeWidth={2.5} fill="currentColor" />
        <span>{label}</span>
        <span className="floating-resume-dock__percent">{progressPct}%</span>
      </span>

      <style>{`
        .floating-resume-dock {
          position: fixed;
          left: 50%;
          bottom: max(24px, env(safe-area-inset-bottom));
          z-index: 19;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 0;
          width: 166px;
          min-height: 56px;
          padding: 8px;
          overflow: hidden;
          border-radius: var(--radius-full);
          border: 1px solid var(--shell-surface-border);
          background:
            linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04)),
            color-mix(in srgb, var(--shell-surface-bg) 90%, transparent);
          color: var(--shell-stat-text);
          box-shadow:
            inset 0 0.5px 0 var(--shell-glass-highlight),
            0 18px 48px rgba(31, 25, 45, 0.12),
            0 5px 16px rgba(31, 25, 45, 0.08);
          backdrop-filter: blur(20px) saturate(1.22);
          -webkit-backdrop-filter: blur(20px) saturate(1.22);
          font-family: var(--font-sans);
          text-align: left;
          cursor: pointer;
          transform: translateX(-50%);
          transition:
            width ${SHELL_MOTION.sheetHeightDurationMs}ms ${SHELL_MOTION.easeEmphasized},
            gap ${SHELL_MOTION.streakTextDurationMs}ms ${SHELL_MOTION.easeEmphasized},
            padding ${SHELL_MOTION.streakTextDurationMs}ms ${SHELL_MOTION.easeEmphasized},
            border-color ${SHELL_MOTION.sheetToneDurationMs}ms ease,
            background-color ${SHELL_MOTION.sheetToneDurationMs}ms ease,
            box-shadow ${SHELL_MOTION.sheetToneDurationMs}ms ease,
            transform 160ms ease;
        }

        .floating-resume-dock:hover,
        .floating-resume-dock:focus-visible {
          width: min(548px, calc(100vw - 48px));
          gap: 18px;
          padding: 12px 12px 12px 22px;
          border-color: var(--shell-command-border-hover);
          background:
            linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.06)),
            color-mix(in srgb, var(--shell-surface-bg) 96%, transparent);
          box-shadow:
            inset 0 0.5px 0 var(--shell-glass-highlight),
            0 22px 56px rgba(31, 25, 45, 0.16),
            0 6px 18px rgba(31, 25, 45, 0.1);
          transform: translateX(-50%) translateY(-2px);
        }

        .floating-resume-dock:focus-visible {
          outline: 2px solid var(--color-accent-outline);
          outline-offset: 2px;
        }

        .floating-resume-dock__content {
          display: flex;
          min-width: 0;
          flex-direction: column;
          gap: 4px;
          max-width: 0;
          opacity: 0;
          overflow: hidden;
          transform: translate3d(8px, 0, 0);
          transition:
            max-width ${SHELL_MOTION.sheetHeightDurationMs}ms ${SHELL_MOTION.easeEmphasized},
            opacity ${SHELL_MOTION.streakFadeDurationMs}ms ease-out,
            transform ${SHELL_MOTION.streakTextDurationMs}ms ${SHELL_MOTION.easeEmphasized};
        }

        .floating-resume-dock:hover .floating-resume-dock__content,
        .floating-resume-dock:focus-visible .floating-resume-dock__content {
          max-width: 332px;
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        .floating-resume-dock__status {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: var(--tracking-eyebrow);
          line-height: 1.1;
          text-transform: uppercase;
          color: var(--color-accent-text);
        }

        .floating-resume-dock__title {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-family: var(--font-sans);
          font-size: 14px;
          font-weight: 650;
          line-height: 1.16;
          letter-spacing: var(--tracking-normal);
          color: var(--color-text);
        }

        .floating-resume-dock__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-width: 142px;
          height: 44px;
          padding: 0 17px;
          border-radius: var(--radius-full);
          background: transparent;
          color: var(--shell-stat-text);
          font-size: 13.5px;
          font-weight: 700;
          letter-spacing: var(--tracking-normal);
          box-shadow: none;
          transition:
            background ${SHELL_MOTION.sheetToneDurationMs}ms ease,
            color ${SHELL_MOTION.sheetToneDurationMs}ms ease,
            box-shadow ${SHELL_MOTION.sheetToneDurationMs}ms ease;
        }

        .floating-resume-dock:hover .floating-resume-dock__action,
        .floating-resume-dock:focus-visible .floating-resume-dock__action {
          background: var(--color-accent-subtle);
          color: var(--color-accent-text);
          box-shadow: inset 0 0.5px 0 var(--shell-glass-highlight);
        }

        .floating-resume-dock__percent {
          font-weight: 650;
          color: var(--color-text-muted);
          opacity: 1;
        }

        .floating-resume-dock:hover .floating-resume-dock__percent,
        .floating-resume-dock:focus-visible .floating-resume-dock__percent {
          color: inherit;
          opacity: 0.78;
        }

        @media (max-width: ${SHELL_BREAKPOINTS.desktop - 1}px) {
          .floating-resume-dock {
            bottom: max(16px, env(safe-area-inset-bottom));
            width: min(420px, calc(100vw - 24px));
            min-height: 68px;
            gap: 12px;
            padding: 10px 10px 10px 18px;
          }

          .floating-resume-dock:hover,
          .floating-resume-dock:focus-visible {
            transform: translateX(-50%) translateY(-1px);
          }

          .floating-resume-dock__status {
            font-size: 10px;
          }

          .floating-resume-dock__content {
            max-width: none;
            opacity: 1;
            transform: none;
            flex: 1;
          }

          .floating-resume-dock__title {
            font-size: 13.5px;
          }

          .floating-resume-dock__action {
            min-width: 120px;
            height: 46px;
            padding: 0 14px;
            font-size: 13px;
          }
        }
      `}</style>
    </button>
  );
}

// ─── Learner Shell ───────────────────────────────────────────────────────────

export default function LearnerShell({ children }: { children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const desktopMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const rightActionsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── CommandBar chat state ──────────────────────────────────────────────────
  const chatState = useCommandBarChat();
  const [commandBarOpen, setCommandBarOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'create' | 'revise' | null>(null);
  const [floatingHeaderActive, setFloatingHeaderActive] = useState(false);
  const [headerRevealed, setHeaderRevealed] = useState(true);
  const [headerTransitionEnabled, setHeaderTransitionEnabled] = useState(true);
  const lastScrollYRef = useRef(0);
  const lastScrollTsRef = useRef(0);
  const upScrollAccumRef = useRef(0);
  const downScrollAccumRef = useRef(0);
  const activateBlockedUntilTsRef = useRef(0);
  const firstRevealFrameRef = useRef<number | null>(null);
  const secondRevealFrameRef = useRef<number | null>(null);
  const thirdRevealFrameRef = useRef<number | null>(null);
  const cancelQueuedFirstReveal = useCallback(() => {
    if (firstRevealFrameRef.current !== null) {
      cancelAnimationFrame(firstRevealFrameRef.current);
      firstRevealFrameRef.current = null;
    }
    if (secondRevealFrameRef.current !== null) {
      cancelAnimationFrame(secondRevealFrameRef.current);
      secondRevealFrameRef.current = null;
    }
    if (thirdRevealFrameRef.current !== null) {
      cancelAnimationFrame(thirdRevealFrameRef.current);
      thirdRevealFrameRef.current = null;
    }
  }, []);
  const resetFirstRevealAnimation = useCallback(() => {
    cancelQueuedFirstReveal();
    setHeaderTransitionEnabled(true);
  }, [cancelQueuedFirstReveal]);

  // Track whether we're at the desktop breakpoint (pill only expands on desktop)
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(min-width: ${SHELL_BREAKPOINTS.desktop}px)`).matches
      : true
  );
  const [isDoubleDesktop, setIsDoubleDesktop] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(min-width: ${SHELL_BREAKPOINTS.doubleDesktop}px)`).matches
      : true
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${SHELL_BREAKPOINTS.desktop}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${SHELL_BREAKPOINTS.doubleDesktop}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDoubleDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const showActions =
    commandBarOpen && isDesktop && chatState.messages.length === 0 && !chatState.isStreaming;
  const hasThread = commandBarOpen && isDesktop && !showActions;
  const shellInlinePad = 'var(--page-gutter)';
  const shellPanelInsetBottom = Math.round(
    isDesktop ? SHELL_DIMENSIONS.panelBottomPadDesktop : SHELL_DIMENSIONS.panelBottomPadMobile
  );
  const navbarHeight = Math.round(
    isDesktop ? SHELL_DIMENSIONS.navHeightDesktop : SHELL_DIMENSIONS.navHeightMobile
  );
  const headerInsetY = Math.round(
    isDesktop ? SHELL_DIMENSIONS.blockPadDesktop : SHELL_DIMENSIONS.blockPadMobile
  );
  const shellHeight = navbarHeight + headerInsetY * 2;
  const shouldFloat = floatingHeaderActive || commandBarOpen;
  const glassyHeader = shouldFloat;
  const shellShown = shouldFloat ? commandBarOpen || headerRevealed : true;
  const shellStickyTop = shouldFloat ? 0 : -shellHeight;
  const expandedSurfaceHeight = hasThread ? 448 : showActions ? 272 : navbarHeight;
  const expandedPanelHeight = Math.max(0, expandedSurfaceHeight - navbarHeight);
  const glassSheetHeight = shellHeight + (commandBarOpen ? expandedPanelHeight : 0);
  const rowBottomInset = commandBarOpen ? 0 : headerInsetY;
  const panelTopInset = commandBarOpen ? headerInsetY : 0;
  const shellGlassBackground = SHELL_GLASS.background;
  const shellGlassBackdrop = SHELL_GLASS.backdrop;
  const shellGlassBorder = SHELL_GLASS.border;
  const shellGlassShadow = SHELL_GLASS.shadow;
  const motionEase = SHELL_MOTION.easeStandard;
  const headerTransition = `transform ${SHELL_MOTION.headerRevealDurationMs}ms ${motionEase}, opacity ${SHELL_MOTION.headerEntranceDurationMs}ms ${motionEase}`;
  const effectiveHeaderTransition = headerTransitionEnabled ? headerTransition : 'none';
  const glassSheetTransition = [
    `height ${SHELL_MOTION.sheetHeightDurationMs}ms ${motionEase}`,
    `opacity ${SHELL_MOTION.sheetFadeDurationMs}ms ease`,
    `background-color ${SHELL_MOTION.sheetToneDurationMs}ms ease`,
    `border-color ${SHELL_MOTION.sheetToneDurationMs}ms ease`,
    `box-shadow ${SHELL_MOTION.sheetToneDurationMs}ms ease`,
  ].join(', ');
  const streakSurfaceTransition = [
    `width ${SHELL_MOTION.streakWidthDurationMs}ms ${SHELL_MOTION.easeEmphasized}`,
    `background-color ${SHELL_MOTION.streakFadeDurationMs}ms ease`,
    `border-color ${SHELL_MOTION.sheetToneDurationMs}ms ease`,
    `box-shadow ${SHELL_MOTION.sheetToneDurationMs}ms ease`,
  ].join(', ');
  const streakTextTransition = [
    `opacity ${SHELL_MOTION.streakFadeDurationMs}ms ease-out`,
    `transform ${SHELL_MOTION.streakTextDurationMs}ms ${SHELL_MOTION.easeEmphasized}`,
  ].join(', ');
  const expandedPanelTransition = `height ${SHELL_MOTION.sheetHeightDurationMs}ms ${motionEase}`;
  const overlayTransition = `opacity ${SHELL_MOTION.overlayFadeDurationMs}ms ${motionEase}`;

  // Track page scroll to enable floating header only when scrolling up from deeper content.
  useEffect(() => {
    const resetScrollAccumulators = () => {
      upScrollAccumRef.current = 0;
      downScrollAccumRef.current = 0;
    };
    const activateFloatingHeader = () => {
      cancelQueuedFirstReveal();
      // Prime floating mode in a hidden state first, then reveal on the next paint.
      // This makes the first reveal follow the same transform path as later reveals.
      setHeaderTransitionEnabled(false);
      setFloatingHeaderActive(true);
      setHeaderRevealed(false);
      firstRevealFrameRef.current = requestAnimationFrame(() => {
        secondRevealFrameRef.current = requestAnimationFrame(() => {
          setHeaderTransitionEnabled(true);
          thirdRevealFrameRef.current = requestAnimationFrame(() => {
            setHeaderRevealed(true);
            firstRevealFrameRef.current = null;
            secondRevealFrameRef.current = null;
            thirdRevealFrameRef.current = null;
          });
        });
      });
    };

    const onScroll = () => {
      const now = performance.now();
      const rawY = window.scrollY;
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const y = Math.min(maxY, Math.max(0, rawY));
      const lastY = lastScrollYRef.current;
      const lastTs = lastScrollTsRef.current || now;
      const dt = Math.max(1, now - lastTs);
      const delta = y - lastY;
      const absDelta = Math.abs(delta);
      const upVelocity = delta < 0 ? -delta / dt : 0;
      const fastUpward = upVelocity >= SHELL_SCROLL.activateMaxUpVelocityPxPerMs;
      const isShortPage =
        document.documentElement.scrollHeight <= window.innerHeight + SHELL_SCROLL.shortPageSlack;
      const activationFloor = Math.max(
        SHELL_SCROLL.activateAfter,
        Math.min(
          SHELL_SCROLL.activateViewportMax,
          Math.max(
            SHELL_SCROLL.activateViewportMin,
            window.innerHeight * SHELL_SCROLL.activateViewportRatio
          )
        )
      );

      if (commandBarOpen) {
        resetFirstRevealAnimation();
        setFloatingHeaderActive(true);
        setHeaderRevealed(true);
        resetScrollAccumulators();
        lastScrollYRef.current = y;
        lastScrollTsRef.current = now;
        return;
      }

      if (isShortPage || y <= SHELL_SCROLL.topReset) {
        resetFirstRevealAnimation();
        setFloatingHeaderActive(false);
        setHeaderRevealed(true);
        resetScrollAccumulators();
        activateBlockedUntilTsRef.current = 0;
        lastScrollYRef.current = y;
        lastScrollTsRef.current = now;
        return;
      }

      // Safari-style overscroll bounce can jitter the reported scroll position.
      // Ignore additional downward pushes when already at the bottom edge.
      if (y >= maxY - SHELL_SCROLL.edgeEpsilon && delta > 0) {
        lastScrollYRef.current = y;
        lastScrollTsRef.current = now;
        return;
      }

      if (absDelta < SHELL_SCROLL.stepThreshold) {
        lastScrollYRef.current = y;
        lastScrollTsRef.current = now;
        return;
      }

      if (!floatingHeaderActive) {
        if (delta < 0) {
          if (fastUpward) {
            activateBlockedUntilTsRef.current = now + SHELL_SCROLL.activateVelocityCooldownMs;
            upScrollAccumRef.current = 0;
          }
          upScrollAccumRef.current += -delta;
          downScrollAccumRef.current = 0;
        } else {
          downScrollAccumRef.current += delta;
          upScrollAccumRef.current = 0;
        }

        // In "page" mode, only switch to floating mode on a deliberate upward scroll.
        if (
          upScrollAccumRef.current >= SHELL_SCROLL.activateUpDistance &&
          y > activationFloor &&
          now >= activateBlockedUntilTsRef.current &&
          !fastUpward
        ) {
          activateFloatingHeader();
          resetScrollAccumulators();
        }
        lastScrollYRef.current = y;
        lastScrollTsRef.current = now;
        return;
      }

      if (delta < 0) {
        upScrollAccumRef.current += -delta;
        downScrollAccumRef.current = 0;
        if (upScrollAccumRef.current >= SHELL_SCROLL.revealUpDistance) {
          setHeaderRevealed(true);
          upScrollAccumRef.current = 0;
        }
      } else {
        downScrollAccumRef.current += delta;
        upScrollAccumRef.current = 0;
        if (
          downScrollAccumRef.current >= SHELL_SCROLL.hideDownDistance &&
          y > SHELL_SCROLL.activateAfter
        ) {
          setHeaderRevealed(false);
          downScrollAccumRef.current = 0;
        }
      }

      lastScrollYRef.current = y;
      lastScrollTsRef.current = now;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [cancelQueuedFirstReveal, commandBarOpen, floatingHeaderActive, resetFirstRevealAnimation]);

  useEffect(() => {
    return () => cancelQueuedFirstReveal();
  }, [cancelQueuedFirstReveal]);

  // Lock page scroll while header is expanded
  useEffect(() => {
    if (commandBarOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.documentElement.style.overflow = '';
      };
    }
  }, [commandBarOpen]);

  // Keep sticky offsets and shell rhythm tokens in sync with the live shell geometry.
  useEffect(() => {
    const root = document.documentElement;
    const vars = {
      '--navbar-height': `${shellHeight}px`,
      '--shell-inline-pad': shellInlinePad,
      '--shell-block-pad': `${headerInsetY}px`,
      '--shell-panel-bottom-pad': `${shellPanelInsetBottom}px`,
    } as const;
    const previous = Object.fromEntries(
      Object.keys(vars).map((name) => [name, root.style.getPropertyValue(name)])
    ) as Record<keyof typeof vars, string>;
    for (const [name, value] of Object.entries(vars)) {
      root.style.setProperty(name, value);
    }
    return () => {
      for (const name of Object.keys(vars) as Array<keyof typeof vars>) {
        if (previous[name]) {
          root.style.setProperty(name, previous[name]);
        } else {
          root.style.removeProperty(name);
        }
      }
    };
  }, [shellHeight, shellInlinePad, headerInsetY, shellPanelInsetBottom]);

  // ── Session guard logic ────────────────────────────────────────────────────
  const createReviseSession = useCreateReviseSession();
  const { guardSession } = useSessionGuard();

  const handleStartSession = useCallback(() => {
    if (!chatState.pendingSession) return;
    guardSession({
      onProceed: async () => {
        try {
          await createReviseSession.mutateAsync({
            stepVersionIds: chatState.pendingSession!.stepVersionIds,
            lessonIds: chatState.pendingSession!.lessonIds,
          });
          navigate('/revision/session', { state: { newSession: true } });
          chatState.clear();
          inputRef.current?.blur();
        } catch {
          // leave expanded
        }
      },
    });
  }, [createReviseSession, navigate, inputRef, guardSession, chatState]);

  // ── Submit handler ─────────────────────────────────────────────────────────
  const handleSubmit = useCallback(
    (text: string, slashCommand: 'create' | 'revise' | null) => {
      const isFirst = chatState.messages.length === 0;
      const prefixed =
        slashCommand && isFirst && !text.startsWith('[Mode:')
          ? `[Mode: ${slashCommand}] ${text}`
          : text;
      void chatState.send(prefixed);
    },
    [chatState]
  );

  // ── Action box handler ────────────────────────────────────────────────────
  const handleSelectAction = useCallback(
    (mode: 'create' | 'revise' | 'search') => {
      if (mode === 'search') {
        // Search has no slash command — just focus the input
        setSelectedMode(null);
      } else {
        setSelectedMode(mode);
      }
      inputRef.current?.focus();
    },
    [inputRef]
  );

  // ── Dismiss handler ────────────────────────────────────────────────────────
  const handleDismiss = useCallback(() => {
    chatState.clear();
    setSelectedMode(null);
    setCommandBarOpen(false);
    inputRef.current?.blur();
  }, [chatState, inputRef]);

  const openCommandBar = useCallback(() => {
    setMenuOpen(false);
    setCommandBarOpen(true);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((v) => {
      const next = !v;
      if (next) {
        chatState.clear();
        setSelectedMode(null);
        setCommandBarOpen(false);
        inputRef.current?.blur();
      }
      return next;
    });
  }, [chatState, inputRef]);

  // Eagerly prefetch primary nav routes after the current page settles
  useEffect(() => {
    const id = setTimeout(() => prefetchPrimaryRoutes(queryClient), 2000);
    return () => clearTimeout(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Notifications ──────────────────────────────────────────────────────────
  const { data: notifData } = useNotifications({ limit: 5 });
  const unreadCount = notifData?.unreadCount ?? 0;

  const prevUnreadRef = useRef<number | null>(null);

  useEffect(() => {
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = unreadCount;

    if (prev === null) return;
    if (unreadCount <= prev) return;

    playNotificationSound();

    const newest = notifData?.notifications?.[0];
    if (newest) {
      const link = getNotificationLink(newest);
      toast(newest.title, {
        description: newest.body ?? undefined,
        action: link
          ? {
              label: 'View',
              onClick: () => navigate(link),
            }
          : undefined,
      });
    }
  }, [unreadCount, notifData, navigate]);

  // ── Streak stat ────────────────────────────────────────────────────────────
  const { data: stats, isSuccess: hasStats } = useMyStats({ staleTime: 5 * 60_000 });
  const { data: suggestionsData } = useMySuggestions({ staleTime: 5 * 60_000, limit: 5 });
  const streakDays = stats?.streak?.currentStreak ?? 0;
  const showStreakStat = hasStats;
  const [isStreakHovered, setIsStreakHovered] = useState(false);
  const streakPillHeight = Math.round(
    isDesktop ? SHELL_DIMENSIONS.streakPillDesktop : SHELL_DIMENSIONS.streakPillMobile
  );
  const streakDigits = Math.max(String(streakDays || 0).length, 1);
  const streakLabel = `${streakDays || 0} ${streakDays === 1 ? 'day' : 'days'}`;
  const streakDisplayText = isDoubleDesktop ? streakLabel : String(streakDays || 0);
  const streakMeasureRef = useRef<HTMLSpanElement>(null);
  const [streakMeasuredTextWidth, setStreakMeasuredTextWidth] = useState(0);
  useEffect(() => {
    const node = streakMeasureRef.current;
    if (!node) return;
    setStreakMeasuredTextWidth(Math.ceil(node.getBoundingClientRect().width));
  }, [streakDisplayText]);
  const avatarSize = Math.round(
    isDesktop ? SHELL_DIMENSIONS.avatarDesktop : SHELL_DIMENSIONS.avatarMobile
  );
  const avatarTopOffset = Math.max(0, (streakPillHeight - avatarSize) / 2);
  const streakTextGap = SHELL_DIMENSIONS.streakTextGap;
  const streakTextRightInset = avatarTopOffset + avatarSize + streakTextGap;
  const streakFallbackTextWidth = isDoubleDesktop
    ? Math.ceil(streakLabel.length * SHELL_DIMENSIONS.streakTextCharWidth)
    : Math.ceil(streakDigits * SHELL_DIMENSIONS.streakDigitCharWidth);
  const streakTextWidth = streakMeasuredTextWidth || streakFallbackTextWidth;
  const streakIconSize = isDesktop ? SHELL_DIMENSIONS.flameDesktop : SHELL_DIMENSIONS.flameMobile;
  const streakExpandedWidth = Math.round(
    avatarTopOffset * 2 +
      avatarSize +
      streakTextGap +
      streakIconSize +
      SHELL_DIMENSIONS.streakIconTextGap +
      streakTextWidth +
      SHELL_DIMENSIONS.streakTextPadLeft
  );
  const streakPillWidth = showStreakStat ? streakExpandedWidth : streakPillHeight;
  const logoSize = Math.round(
    isDesktop ? SHELL_DIMENSIONS.logoDesktop : SHELL_DIMENSIONS.logoMobile
  );
  const commandBarMaxWidth = 'var(--shell-command-max-width)';
  const [rightActionsWidth, setRightActionsWidth] = useState(0);

  const measureRightActions = useCallback(() => {
    const node = rightActionsRef.current;
    if (!node) return;
    setRightActionsWidth(Math.ceil(node.getBoundingClientRect().width));
  }, []);

  useLayoutEffect(() => {
    measureRightActions();
    const node = rightActionsRef.current;
    const observer =
      typeof ResizeObserver !== 'undefined' && node
        ? new ResizeObserver(() => measureRightActions())
        : null;
    if (observer && node) {
      observer.observe(node);
    }
    window.addEventListener('resize', measureRightActions);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measureRightActions);
    };
  }, [measureRightActions, isDesktop, isDoubleDesktop, streakPillWidth]);

  const expandedRightColumnWidth = Math.max(rightActionsWidth, streakPillWidth);
  const expandedGridTemplate = isDesktop
    ? `${logoSize}px var(--shell-logo-command-gap) minmax(0, 1fr) var(--shell-command-actions-gap) ${expandedRightColumnWidth}px`
    : `${logoSize}px var(--shell-logo-command-gap) minmax(0, 1fr)`;

  // ── ⌘K shortcut ────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // ── Entrance animation ─────────────────────────────────────────────────────
  const [headerVisible, setHeaderVisible] = useState(false);
  useEffect(() => {
    // Trigger on next frame so the initial translate is painted first
    const id = requestAnimationFrame(() => setHeaderVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // ── Cycling placeholder ─────────────────────────────────────────────────────
  const placeholders = buildCommandBarPlaceholders(stats, suggestionsData);

  return (
    <div
      className="flex min-h-screen flex-col overflow-x-clip"
      style={{
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <LearnerTopLoadingBar />

      {/* ── Sticky header wrapper — fixed layout height, never shifts content ── */}
      <header
        className="sticky z-30"
        style={{
          top: shellStickyTop,
          height: shellHeight,
          transform: headerVisible
            ? shellShown
              ? 'translateY(0)'
              : 'translateY(-100%)'
            : 'translateY(-100%)',
          opacity: headerVisible ? 1 : 0,
          transition: effectiveHeaderTransition,
          willChange: 'transform, opacity',
        }}
        onMouseDown={(e) => {
          // Prevent clicks within the header from stealing focus from the input,
          // which would trigger onBlur and collapse the expanded area.
          // Only suppress when expanded — normal clicks (logo, menu) still work when collapsed.
          if (commandBarOpen && e.target !== inputRef.current) {
            e.preventDefault();
          }
        }}
      >
        {/* Unified glass sheet — row + expanded content share one continuous surface */}
        <div
          className="absolute inset-x-0 top-0"
          style={{
            height: glassSheetHeight,
            overflow: 'hidden',
            opacity: shellShown ? 1 : 0,
            pointerEvents: shellShown ? 'auto' : 'none',
            background: glassyHeader ? shellGlassBackground : 'var(--color-bg)',
            backdropFilter: glassyHeader ? shellGlassBackdrop : 'none',
            WebkitBackdropFilter: glassyHeader ? shellGlassBackdrop : 'none',
            borderBottom: `1px solid ${glassyHeader ? shellGlassBorder : 'transparent'}`,
            boxShadow: glassyHeader ? shellGlassShadow : 'none',
            transition: glassSheetTransition,
          }}
        >
          <div
            className="mx-auto"
            style={{
              maxWidth: 'var(--page-max-width)',
              paddingTop: headerInsetY,
              paddingBottom: rowBottomInset,
            }}
          >
            <div
              className="flex shrink-0 items-center"
              style={{
                height: navbarHeight,
                paddingInline: shellInlinePad,
                gap: isDesktop ? 0 : 8,
              }}
            >
              {/* ── Left zone: Brand mark ──────────────────────────────── */}
              <Link
                to="/"
                className="flex shrink-0 items-center transition-transform duration-200 hover:scale-[1.06] active:scale-[0.96]"
                aria-label="Home"
                style={{
                  marginRight: isDesktop ? 'var(--shell-logo-command-gap)' : undefined,
                }}
              >
                <span
                  className="[&>svg]:size-full"
                  style={{ display: 'block', width: logoSize, height: logoSize }}
                >
                  <Logomark />
                </span>
              </Link>

              {/* ── Desktop command bar: sits in the same left flow as the logo */}
              <div className="hidden min-w-0 flex-1 learner-single:block">
                <div className="w-full" style={{ maxWidth: commandBarMaxWidth }}>
                  <CommandBar
                    inputRef={inputRef}
                    placeholders={placeholders}
                    expanded={commandBarOpen}
                    sizeScale={1.08}
                    disabled={chatState.isStreaming}
                    hasMessages={chatState.messages.length > 0}
                    externalSlashCommand={selectedMode}
                    staticPlaceholder={
                      chatState.pendingJob
                        ? 'Your lesson is on its way...'
                        : chatState.suggestedPlaceholder
                    }
                    onSubmit={handleSubmit}
                    onDismiss={handleDismiss}
                    onFocus={() => setCommandBarOpen(true)}
                    onBlur={() => {
                      // Ignore blur caused by CSS-hiding the input on resize
                      if (!inputRef.current?.offsetParent) return;
                      if (chatState.messages.length === 0 && !chatState.isStreaming) {
                        setCommandBarOpen(false);
                      }
                    }}
                  />
                </div>
              </div>

              {/* ── Centre zone: Mobile trigger (<640px) ──────────────────── */}
              <div className="mx-2 flex flex-1 learner-single:hidden">
                <CommandBarTrigger placeholders={placeholders} onOpen={openCommandBar} />
              </div>

              {/* ── Right zone: Streak + Avatar (desktop) / Hamburger (mobile) */}
              <div
                ref={rightActionsRef}
                className="flex shrink-0 items-center justify-end gap-2 learner-double:gap-3"
                style={{
                  marginLeft: isDesktop ? 'var(--shell-command-actions-gap)' : undefined,
                  gap: isDoubleDesktop ? 'var(--shell-nav-streak-gap)' : undefined,
                }}
              >
                <nav
                  className="hidden shrink-0 items-center learner-double:flex"
                  style={{ gap: 'var(--shell-nav-gap)' }}
                  aria-label="Learner navigation"
                >
                  <ShellNavLink to="/my-courses">Courses</ShellNavLink>
                  <ShellNavLink to="/lessons">My Lessons</ShellNavLink>
                  <ShellNavLink to="/discover">Discover</ShellNavLink>
                </nav>
                {/* Desktop: unified streak + avatar cluster */}
                <div
                  className="relative hidden learner-single:block"
                  style={{
                    width: streakPillWidth,
                    height: streakPillHeight,
                    transition: streakSurfaceTransition,
                    willChange: 'width',
                  }}
                >
                  <span
                    ref={streakMeasureRef}
                    aria-hidden
                    className="pointer-events-none absolute top-0 left-0 whitespace-nowrap"
                    style={{
                      visibility: 'hidden',
                      fontSize: 14,
                      fontWeight: 560,
                      fontFamily: 'var(--font-sans)',
                    }}
                  >
                    {streakDisplayText}
                  </span>
                  <button
                    ref={desktopMenuTriggerRef}
                    type="button"
                    onClick={toggleMenu}
                    onMouseEnter={() => setIsStreakHovered(true)}
                    onMouseLeave={() => setIsStreakHovered(false)}
                    className="relative block rounded-full"
                    style={{
                      width: streakPillWidth,
                      height: streakPillHeight,
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      overflow: 'visible',
                      transition: streakSurfaceTransition,
                      willChange: 'width',
                    }}
                    aria-label={
                      showStreakStat
                        ? `Menu, ${streakDays} ${streakDays === 1 ? 'day' : 'days'} streak`
                        : 'Menu'
                    }
                  >
                    <span
                      aria-hidden
                      className="absolute top-0 right-0 rounded-full"
                      style={{
                        width: showStreakStat ? streakPillWidth : streakPillHeight,
                        height: streakPillHeight,
                        background: showStreakStat
                          ? 'var(--shell-command-bg)'
                          : 'var(--color-surface-solid)',
                        border: `1px solid ${
                          isStreakHovered
                            ? 'var(--shell-command-border-hover)'
                            : 'var(--shell-command-border)'
                        }`,
                        boxSizing: 'border-box',
                        backdropFilter: showStreakStat ? 'blur(12px) saturate(1.2)' : 'none',
                        WebkitBackdropFilter: showStreakStat ? 'blur(12px) saturate(1.2)' : 'none',
                        boxShadow: isStreakHovered
                          ? 'inset 0 0.5px 0 var(--shell-glass-highlight), 0 1px 3px rgba(0,0,0,0.05)'
                          : 'inset 0 0.5px 0 var(--shell-glass-highlight), 0 1px 2px rgba(0,0,0,0.03)',
                        transition: streakSurfaceTransition,
                        willChange: 'width, background-color, border-color, box-shadow',
                        pointerEvents: 'none',
                      }}
                    />
                    <span
                      className="pointer-events-none absolute top-0 flex h-full items-center gap-1 whitespace-nowrap"
                      style={{
                        right: streakTextRightInset,
                        opacity: showStreakStat ? 1 : 0,
                        transform: showStreakStat ? 'translate3d(0,0,0)' : 'translate3d(6px,0,0)',
                        fontSize: 14,
                        fontWeight: 560,
                        color: 'var(--shell-stat-text)',
                        transition: streakTextTransition,
                        transitionDelay: showStreakStat
                          ? `${SHELL_MOTION.streakRevealDelayMs}ms, ${SHELL_MOTION.streakRevealDelayMs}ms`
                          : '0ms, 0ms',
                        willChange: 'opacity, transform',
                      }}
                      aria-hidden={!showStreakStat}
                    >
                      <Flame
                        size={SHELL_DIMENSIONS.flameDesktop}
                        strokeWidth={2.2}
                        style={{ color: 'var(--shell-stat-text)' }}
                      />
                      <span>{streakDays}</span>
                      <span className="hidden learner-double:inline">
                        {streakDays === 1 ? 'day' : 'days'}
                      </span>
                    </span>
                    <span
                      className="absolute z-[1]"
                      style={{
                        top: avatarTopOffset,
                        right: avatarTopOffset,
                      }}
                    >
                      <Avatar hasUnread={unreadCount > 0} size={avatarSize} />
                    </span>
                  </button>
                </div>

                {/* Mobile: hamburger + streak */}
                <div className="flex items-center gap-2.5 learner-single:hidden">
                  {showStreakStat && (
                    <div
                      className="flex items-center gap-1 whitespace-nowrap"
                      style={{
                        fontSize: 13,
                        fontWeight: 550,
                        color: 'var(--shell-stat-text)',
                      }}
                    >
                      <Flame size={SHELL_DIMENSIONS.flameMobile} strokeWidth={2.2} />
                      <span>{streakDays}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={toggleMenu}
                    className="flex shrink-0 items-center justify-center rounded-full"
                    style={{
                      width: 38,
                      height: 38,
                      background: 'var(--shell-command-bg)',
                      border: '1px solid var(--shell-command-border)',
                    }}
                    aria-label="Menu"
                  >
                    <MenuIcon size={18} strokeWidth={2} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Expanded command area lives in the same sheet for seamless expansion */}
          <div
            className="hidden learner-single:block"
            style={{
              height: commandBarOpen ? expandedPanelHeight + panelTopInset : 0,
              pointerEvents: commandBarOpen ? 'auto' : 'none',
              transition: expandedPanelTransition,
            }}
          >
            <div
              className="mx-auto h-full"
              style={{
                maxWidth: 'var(--page-max-width)',
                paddingInline: shellInlinePad,
                paddingTop: panelTopInset,
                paddingBottom: shellPanelInsetBottom,
              }}
            >
              <div
                className="grid h-full items-start"
                style={{ gridTemplateColumns: expandedGridTemplate }}
              >
                <div className="min-w-0" style={{ gridColumn: '3 / 4' }}>
                  {showActions ? (
                    <CommandBarActions onSelect={handleSelectAction} />
                  ) : (
                    <CommandBarThread
                      messages={chatState.messages}
                      isStreaming={chatState.isStreaming}
                      pendingSession={chatState.pendingSession}
                      pendingJob={chatState.pendingJob}
                      suggestions={chatState.suggestions}
                      activeToolCalls={chatState.activeToolCalls}
                      onStartSession={handleStartSession}
                      onSendSuggestion={(text) => void chatState.send(text)}
                    />
                  )}
                </div>

                {isDoubleDesktop && (
                  <div className="min-w-0" style={{ gridColumn: '5 / 6' }}>
                    <CommandPanelPlaceholder />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Menus — rendered outside row/panel surfaces so overflow doesn't clip them */}
        <div className="hidden learner-single:block">
          <ExpandableMenu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            notifData={notifData}
            anchorRef={desktopMenuTriggerRef}
          />
        </div>
        <div className="learner-single:hidden">
          <ExpandableMenu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            notifData={notifData}
            mobile
          />
        </div>
      </header>

      {/* Mobile AI chat modal */}
      <div className="learner-single:hidden">
        <CommandBarModal
          open={commandBarOpen}
          messages={chatState.messages}
          isStreaming={chatState.isStreaming}
          pendingSession={chatState.pendingSession}
          pendingJob={chatState.pendingJob}
          suggestions={chatState.suggestions}
          activeToolCalls={chatState.activeToolCalls}
          placeholders={placeholders}
          selectedMode={selectedMode}
          staticPlaceholder={chatState.suggestedPlaceholder}
          onSubmit={handleSubmit}
          onDismiss={handleDismiss}
          onStartSession={handleStartSession}
          onSendSuggestion={(text) => void chatState.send(text)}
          onSelectAction={handleSelectAction}
        />
      </div>

      {/* Blur overlay — covers page content when expanded, sits below sticky header (desktop only) */}
      <div
        className="fixed inset-0 z-20 hidden learner-single:block"
        style={{
          backdropFilter: commandBarOpen ? 'blur(6px)' : 'none',
          WebkitBackdropFilter: commandBarOpen ? 'blur(6px)' : 'none',
          background: commandBarOpen ? 'rgba(0,0,0,0.08)' : 'transparent',
          opacity: commandBarOpen ? 1 : 0,
          pointerEvents: commandBarOpen ? 'auto' : 'none',
          transition: overlayTransition,
        }}
        onClick={handleDismiss}
      />

      <FloatingResumeButton hidden={commandBarOpen || menuOpen} />

      {/* Content */}
      <main className="mx-auto w-full flex-1" style={{ maxWidth: 'var(--page-max-width)' }}>
        {children}
      </main>
    </div>
  );
}
